import React, { useEffect, useMemo, useRef, useState } from "react";
import telecallerIcon from "../assets/images/icon-avtar.png";
import "./TopBar.css";
import API from "../api";
import SideDrawer from "./SideDrawer";
import NotificationsFeed from "../pages/NotificationsFeed";
import useNotificationsCount from "../hooks/useNotificationsCount";
import LeadDetailsModal from "../pages/Lead/LeadDetailsModal";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUser, setUser, logout as logoutAction } from '../store/authSlice';


export default function TopBar() {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifCount = useNotificationsCount();
  const [googleConnected, setGoogleConnected] = useState(null); // null=loading, true/false

  const user = useSelector(selectUser);
  const role = useMemo(() => (user?.role || "").toLowerCase(), [user]);
  const ass = useMemo(() => (user?.ass ?? 2), [user]);

  const handleAvatarClick = () => navigate("/profile");

   const dispatch = useDispatch();
   const navigate = useNavigate();

  // ==== live search state ====
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [openDrop, setOpenDrop] = useState(false);
  const [searching, setSearching] = useState(false);
  const ctrlRef = useRef(null);
  const barRef = useRef(null);

  // Modal for a selected lead
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalLead, setModalLead] = useState(null);


  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!user) {
          const { data } = await API.get('/auth/me');
          if (!ignore && data?.user) dispatch(setUser(data.user));
        }
      } catch (_) {}
    })();
    return () => { ignore = true; };
  }, [user, dispatch]);

  useEffect(() => {
    if (!user?.email) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await API.get('/google/status', { params: { email: user.email } });
        if (!ignore) setGoogleConnected(!!data?.connected);
      } catch (_) {
        if (!ignore) setGoogleConnected(false);
      }
    })();
    return () => { ignore = true; };
  }, [user?.email]);


  // close dropdown when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (!barRef.current) return;
      if (!barRef.current.contains(e.target)) setOpenDrop(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced search (real-time)
  useEffect(() => {
    // min 2 chars
    if (q.trim().length < 2) {
      setResults([]);
      setOpenDrop(!!q.trim());
      return;
    }

    setSearching(true);

    // abort previous
    if (ctrlRef.current) ctrlRef.current.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;

    const t = setTimeout(async () => {
      try {
        const resp = await API.get("/auth/search-leads", {
          params: { q, role, ass },
          signal: ctrl.signal,
        });
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        setResults(rows);
        setOpenDrop(true);
      } catch (e) {
        // ignore abort errors
        if (e?.name !== "CanceledError" && e?.message !== "canceled") {
          console.error("search error", e?.message);
        }
      } finally {
        setSearching(false);
      }
    }, 250); // debounce

    return () => clearTimeout(t);
  }, [q, role, ass]);

  const openLead = async (id) => {
    if (!id) return;
    setOpenDrop(false);
    try {
      setModalOpen(true);
      setModalLoading(true);
      setModalError("");
      setModalLead(null);
      const resp = await API.get(`/auth/lead/${id}`);
      const lead = resp?.data?.lead || resp?.data;
      setModalLead(lead || null);
    } catch (e) {
      console.error(e);
      setModalError("Could not load lead details.");
    } finally {
      setModalLoading(false);
    }
  };


   const handleLogout = async () => {
    try {
      await API.post('/auth/logout');
    } catch (_) {}
    finally {
      dispatch(logoutAction());
      navigate('/login', { replace: true });
    }
  };

  

  return (
    <>
      <header className="tb">
        <div className="tb__left">
          <span className="tb__logo" aria-hidden="true"> </span>
          <span className="tb__brand">POPUP</span>
        </div>

        {/* Desktop / tablet search */}
        <div className="tb__center" ref={barRef}>
          <label className="tb__search" role="search">
            <svg aria-hidden="true" viewBox="0 0 24 24" className="tb__searchIcon">
              <path
                d="M21 21l-4.3-4.3M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
            <input
              className="tb__searchInput"
              type="text"
              placeholder="Search leads by name, email, phone or business"
              aria-label="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => q.trim() && setOpenDrop(true)}
            />
            {searching && <span className="tb__spinner" aria-hidden="true" />}
          </label>

          {/* Dropdown results */}
          {openDrop && (
            <div className="tb__searchDrop">
              {results.length === 0 ? (
                <div className="tb__searchEmpty">No results</div>
              ) : (
                <ul className="tb__searchList">
                  {results.map(r => (
                    <li key={r.id} className="tb__searchItem" onClick={() => openLead(r.id)}>
                      <div className="tb__searchItemPrimary">
                        <strong>{r.name || "Untitled"}</strong>
                        {r.bname ? <span className="tb__muted"> &nbsp;({r.bname})</span> : null}
                      </div>
                      <div className="tb__searchItemMeta">
                        {r.email && <span>{r.email}</span>}
                        {r.mobile && <span>• {r.mobile}</span>}
                        {r.status && <span className={`tb__badgePill`}>{r.status}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="tb__right">
          {/* Google connection status */}
          {googleConnected === false && (
            <span className="tb__googleStatus tb__googleStatus--error" title="Google account not connected">
              Google not connected
            </span>
          )}
          {googleConnected === true && (
            <span className="tb__googleStatus tb__googleStatus--ok" title="Google account connected">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#34A853"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#4285F4"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            </span>
          )}

          <button onClick={handleLogout} className="tb__iconBtn" aria-label="Logout">
  <svg viewBox="0 0 24 24" fill="none">
    <path
      d="M4 3h9v18H4z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13 12h7M17 8l3 4-3 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
</button>


         

          <button
            className="tb__iconBtn"
            aria-label="Notifications"
            onClick={() => setNotifOpen(true)}
          >
            <svg viewBox="0 0 24 24">
              <path d="M15 17H5l1.2-1.5A6 6 0 0 0 7 12V9a5 5 0 1 1 10 0v3a6 6 0 0 0 .8 3.5L19 17h-4"
                fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 20a2 2 0 0 0 4 0"
                fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span className={`tb__badge ${notifCount ? "" : "tb__badge--hide"}`}>
              {notifCount > 99 ? "99+" : notifCount}
            </span>
          </button>

          <button className="tb__avatarBtn" aria-label="Account" onClick={handleAvatarClick}>
            <img src={telecallerIcon} alt="User avatar" className="tb__avatar" />
          </button>
        </div>
      </header>

      {/* Notification drawer */}
      <SideDrawer open={notifOpen} onClose={() => setNotifOpen(false)} title="Notifications">
        <NotificationsFeed />
      </SideDrawer>

      {/* Mobile search overlay (uses same state 'q' for simplicity) */}
      {mobileSearchOpen && (
        <div className="tbMobileSearch" role="dialog" aria-modal="true">
          <div className="tbMobileSearch__bar">
            <svg aria-hidden="true" viewBox="0 0 24 24" className="tbMobileSearch__icon">
              <path d="M21 21l-4.3-4.3M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                fill="none" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              autoFocus
              className="tbMobileSearch__input"
              type="text"
              placeholder="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className="tbMobileSearch__close" onClick={() => setMobileSearchOpen(false)} aria-label="Close search">✕</button>
          </div>
        </div>
      )}


      

      {/* Lead details modal */}
      <LeadDetailsModal
        open={modalOpen}
        loading={modalLoading}
        error={modalError}
        lead={modalLead}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
